import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Iconify, Label } from "../../components";
import { capitalize } from "lodash";
import {
  Box,
  Tab,
  Tabs,
  Stack,
  Skeleton,
  styled,
  Typography,
  Card,
  useTheme,
} from "@mui/material";
import back from "../../assets/img/icons/back.svg";
import Image from "../../components/image/Image";
import { toast } from "react-toastify";
import { merchantApi } from "../../redux/api/apiConfig";
import * as API from "../../ApiUrl";
import usePageTitle from "../../hooks/usePageTitle";
import { CustomAvatar } from "../../components/customAvatar";
import signinBg from "../../assets/img/signinBg.png";
import {
  handleApiErrors,
} from "../../utils/common";
import { BusinessDetailsTab, BusinessDocumentsTab } from "./components";

const StyledRoot = styled("div")(({ theme }) => ({
  "&:before": {
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    top: 0,
    zIndex: 9,
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
  },
}));

const StyledInfo = styled("div")(({ theme }) => ({
  left: 0,
  right: 0,
  zIndex: 99,
  position: "absolute",
  marginTop: theme.spacing(1),
  [theme.breakpoints.up("md")]: {
    right: "auto",
    display: "flex",
    alignItems: "center",
    left: theme.spacing(3),
    bottom: theme.spacing(5),
  },
}));

const KycSubmittedDetail = () => {
  const [currentTab, setCurrentTab] = useState("business");
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const kycId = query.get("kyc_id");
  const theme = useTheme();

  usePageTitle("KYC Submitted Details", "Review KYC submission details");

  async function getKycDetail() {
    if (!kycId) {
      toast.error("KYC ID is required");
      history.goBack();
      return;
    }

    try {
      setLoading(true);
      const response = await merchantApi.get(API.KYC_ADMIN_DETAIL, {
        params: { kyc_id: kycId },
      });

      if (response.data && response.data.success && response.data.data) {
        setKycData(response.data.data);
      } else {
        toast.error("KYC details not found");
        history.goBack();
      }
      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
      setLoading(false);
      history.goBack();
    }
  }

  useEffect(() => {
    if (kycId) {
      getKycDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycId]);

  const backClickHandler = () => {
    history.goBack();
  };

  const handleKycStatusUpdate = async (action, notes = "") => {
    try {
      await merchantApi.post(API.KYC_ADMIN_APPROVAL, {
        kyc_id: parseInt(kycId),
        action: action,
        notes: notes,
      });
      toast.success(`KYC ${action === "approve" ? "approved" : "rejected"} successfully`);
      // Refresh data after status update
      await getKycDetail();
    } catch (error) {
      const errorMessages = handleApiErrors(error);
      errorMessages.forEach(({ field, message }) => {
        const formattedMessage = field ? `${field}: ${message}` : message;
        toast.error(formattedMessage);
      });
    }
  };

  const PREVIEW_TABS = [
    {
      value: "business",
      icon: <Iconify icon={"ic:round-account-box"} width={20} height={20} />,
      component: (
        <BusinessDetailsTab
          kycData={kycData}
          onStatusUpdate={handleKycStatusUpdate}
        />
      ),
    },
    {
      value: "business documents",
      icon: <Iconify icon={"solar:document-bold"} width={20} height={20} />,
      component: <BusinessDocumentsTab kycData={kycData} />,
    },
  ];

  const getStatusColor = (status) => {
    if (!status) return "application submitted";
    const statusLower = status.toLowerCase();
    if (statusLower === "approved" || statusLower === "active") {
      return "complete";
    } else if (statusLower === "rejected") {
      return "rejected";
    } else if (statusLower === "pending_review") {
      return "application submitted";
    }
    return "application submitted";
  };

  const displayName =
    kycData?.legal_name ||
    kycData?.business_name ||
    kycData?.name ||
    "KYC Submission";

  return (
    <Box sx={{ padding: "24px " }}>
      {loading ? (
        <Skeleton variant="rounded" animation={"wave"} height={1500} />
      ) : (
        <>
          <Stack justifyContent={"space-between"} direction={"row"}>
            <span onClick={backClickHandler} className="back mb-4">
              <img src={back} alt="back" />
              <span>Back</span>
            </span>
          </Stack>
          <Card
            sx={{
              mb: 3,
              height: 200,
              position: "relative",
            }}
          >
            <StyledRoot>
              <StyledInfo>
                <Stack sx={{ position: "relative", width: "fit-content" }}>
                  <CustomAvatar
                    alt={displayName}
                    name={displayName}
                    sx={{
                      mx: "auto",
                      borderWidth: 2,
                      borderStyle: "solid",
                      borderColor: "common.white",
                      width: { xs: 80, md: 128 },
                      height: { xs: 80, md: 128 },
                      fontSize: "40px",
                    }}
                  />
                </Stack>
                <Box
                  sx={{
                    ml: { md: 3 },
                    mt: { xs: 1, md: 0 },
                    color: "common.white",
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  <Stack
                    direction={"column"}
                    alignItems={"flex-start"}
                    justifyContent={"center"}
                    spacing={1}
                  >
                    <Stack direction={"row"} gap={1} alignItems={"center"}>
                      <Typography variant="h4" sx={{ color: "white" }}>
                        {capitalize(displayName)}
                      </Typography>
                      {kycData?.kyc_review_status && (
                        <Label
                          variant={"outlined"}
                          color={getStatusColor(kycData.kyc_review_status)}
                          sx={{ textTransform: "capitalize", fontSize: "14px" }}
                        >
                          {kycData.kyc_review_status.replace(/_/g, " ")}
                        </Label>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </StyledInfo>

              <Image
                alt="cover"
                src={signinBg}
                sx={{
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  position: "absolute",
                  objectFit: "cover",
                }}
              />
            </StyledRoot>
          </Card>

          <Stack
            justifyContent={"space-between"}
            alignItems={{ md: "center", xs: "start" }}
            direction={{ md: "row", xs: "column" }}
            spacing={2}
          >
            <Tabs
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{
                background: theme.palette.common.lightAccentColor,
                width: "100%",
                borderRadius: "7px",
                "& .MuiTab-labelIcon": {
                  color: theme.palette.common.accentColor,
                },
              }}
            >
              {PREVIEW_TABS.map((tab) => (
                <Tab
                  disableRipple
                  key={tab.value}
                  label={capitalize(tab.value)}
                  iconPosition="start"
                  icon={tab.icon}
                  value={tab.value}
                />
              ))}
            </Tabs>
          </Stack>
          {PREVIEW_TABS.map((tab) => {
            const isMatched = tab.value === currentTab;
            return isMatched && <Box key={tab.value}>{tab.component}</Box>;
          })}
        </>
      )}
    </Box>
  );
};

export default KycSubmittedDetail;

